import { G } from '@/constants/';
import { PowerProvider } from '@/physics/power/';
import { Course } from '@/types/course/';
import { Path } from '@/types/path/';

/**
 * Provides rolling resistance power calculations for virtual cycling.
 *
 * Rolling resistance is the resistive force generated by tire deformation and
 * friction between tires and road surface. This implementation follows the
 * standard rolling resistance model used in cycling physics.
 *
 * Formula: P_rolling = -cos(atan(grade)) * m * g * v * Crr
 *
 * Where:
 * - m: total system mass (cyclist + bike) in kg
 * - g: gravitational acceleration (9.80665 m/s²)
 * - v: velocity in m/s
 * - Crr: coefficient of rolling resistance (dimensionless, typically 0.003-0.005)
 * - grade: road gradient (dimensionless, e.g., 0.05 for 5%)
 *
 * The cosine term accounts for the reduced normal force on inclines/declines.
 *
 * @see PowerProvider
 * @see https://www.sheldonbrown.com/brandt/rolling-resistance.html
 */
class RollingResistancePowerProvider implements PowerProvider {
    /**
     * Calculates the rolling resistance power at a specific location.
     *
     * Rolling resistance is always negative (resistive force) and increases
     * linearly with speed. The cosine term slightly reduces resistance on
     * steep grades due to reduced normal force.
     *
     * @param course The course configuration containing cyclist and bike parameters
     * @param location The current point with speed and grade information
     * @returns Rolling resistance power in watts (always negative or zero)
     */
    getPowerW(course: Course, path: Path, pointIndex: number): number {
        const mKg = course.cyclist.mKg;
        const crr = course.bike.crr;
        const grade = path.getGrade(pointIndex);
        const speed = path.getSpeed(pointIndex);

        // Calculate the cosine of the road angle from grade
        // For small angles: cos(atan(grade)) ≈ 1 / sqrt(1 + grade²)
        const coef = Math.cos(Math.atan(grade));

        // Calculate rolling resistance power (negative = resistance)
        const powerRollingResistance = -coef * mKg * G * speed * crr;

        // Store debug value in the point
        path.setPRollingResistance(pointIndex, powerRollingResistance);

        return powerRollingResistance;
    }
}

export const rollingResistancePowerProvider: PowerProvider = new RollingResistancePowerProvider();
